import React, { useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Alert,
  Chip,
} from '@mui/material';
import { Error as ErrorIcon } from '@mui/icons-material';
import { ColumnMappingData } from '../../pages/ExcelUploadPage';
import { ManualEntry } from './ManualFieldEntry';

interface MappingValidationPanelProps {
  mappings: ColumnMappingData;
  manualEntries?: ManualEntry[];
  legCount: number;
  onValidationChange: (isValid: boolean) => void;
  actionButton?: React.ReactNode;
}

interface ValidationResult {
  isValid: boolean;
  missingRequired: string[];
  unmappedOptional: string[];
  legStatus: Record<number, {
    complete: boolean;
    mappedCount: number;
    totalRequired: number;
  }>;
}

// Origin/destination field variants that satisfy the "at least 1" requirement
const ORIGIN_FIELDS = ['origin_country', 'origin_city', 'origin_postal_code', 'origin_free_address'];
const DEST_FIELDS = ['destination_country', 'destination_city', 'destination_postal_code', 'destination_free_address'];

const getRequiredFields = (legCount: number): string[] => {
  // Only weight_kg and transport_mode are strictly required.
  // Origin/destination are validated separately (at least 1 each per leg).
  const baseRequired: string[] = ['weight_kg'];
  for (let i = 1; i <= Math.max(1, legCount); i++) {
    baseRequired.push(`transport_mode_leg${i}`);
  }
  return baseRequired;
};

const getFieldDisplayName = (fieldId: string): string => {
  const displayNames: Record<string, string> = {
    'shipment_date': 'Shipment Date',
    'customer_name': 'Customer Name',
    'weight_kg': 'Weight (kg)',
    'origin_country': 'Origin Country',
    'destination_country': 'Destination Country',
    'transport_mode': 'Transport Mode',
    'origin_address': 'Origin (country, city, postal code, or free address)',
    'destination_address': 'Destination (country, city, postal code, or free address)',
  };

  // Handle leg-specific fields
  const legMatch = /^(.+)_leg(\d+)$/.exec(fieldId);
  if (legMatch) {
    const baseField = legMatch[1];
    const legNumber = legMatch[2];
    const baseName = displayNames[baseField] || baseField.replaceAll('_', ' ');
    return `${baseName} (Leg ${legNumber})`;
  }

  return displayNames[fieldId] || fieldId.replaceAll('_', ' ');
};

const validateMappings = (
  mappings: ColumnMappingData,
  legCount: number,
  manualEntries: ManualEntry[] = []
): ValidationResult => {
  const requiredFields = getRequiredFields(legCount);
  const mappedFields: string[] = [];
  for (const value of Object.values(mappings)) {
    mappedFields.push(...value.split('||').filter(Boolean));
  }

  // Also consider manual entries as mapped fields
  const manualFieldIds = manualEntries
    .filter(entry => entry.fieldId && entry.value)
    .map(entry => entry.fieldId);

  const allMappedFields = new Set([...mappedFields, ...manualFieldIds]);

  // Check strict required fields (weight, transport_mode)
  const missingRequired = requiredFields.filter(field => !allMappedFields.has(field));

  // Check "at least 1 origin + 1 destination" per leg
  const legStatus: Record<number, any> = {};
  for (let i = 1; i <= legCount; i++) {
    const hasOrigin = ORIGIN_FIELDS.some(f => allMappedFields.has(`${f}_leg${i}`));
    const hasDest = DEST_FIELDS.some(f => allMappedFields.has(`${f}_leg${i}`));
    const hasTransport = allMappedFields.has(`transport_mode_leg${i}`);

    // Total "slots" to fill: origin, destination, transport_mode
    const totalRequired = 3;
    const mappedCount = (hasOrigin ? 1 : 0) + (hasDest ? 1 : 0) + (hasTransport ? 1 : 0);

    if (!hasOrigin) missingRequired.push(`origin_address_leg${i}`);
    if (!hasDest) missingRequired.push(`destination_address_leg${i}`);

    legStatus[i] = {
      complete: hasOrigin && hasDest && hasTransport,
      mappedCount,
      totalRequired,
    };
  }

  return {
    isValid: missingRequired.length === 0,
    missingRequired,
    unmappedOptional: [],
    legStatus,
  };
};

export const MappingValidationPanel: React.FC<MappingValidationPanelProps> = ({
  mappings,
  manualEntries = [],
  legCount,
  onValidationChange,
  actionButton,
}) => {
  const validation = useMemo(() => {
    return validateMappings(mappings, legCount, manualEntries);
  }, [mappings, legCount, manualEntries]);

  useEffect(() => {
    onValidationChange(validation.isValid);
  }, [validation.isValid, onValidationChange]);

  // Total = strict required fields + 2 per leg (origin + destination)
  const totalRequired = getRequiredFields(legCount).length + legCount * 2;
  const totalMapped = totalRequired - validation.missingRequired.length;
  const legPlural = legCount > 1 ? 's' : '';
  const missingFieldPlural = validation.missingRequired.length > 1 ? 's' : '';

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, px: 2, py: 1.5 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        Mapping Status
      </Typography>

      {/* Overall status + action button on same row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: validation.missingRequired.length > 0 ? 1.5 : 0 }}>
        <Alert severity={validation.isValid ? 'success' : 'warning'} sx={{ flex: 1, py: 0.5 }}>
          {validation.isValid
            ? `All required fields mapped (${totalRequired}/${totalRequired}) for ${legCount} leg${legPlural}`
            : `${validation.missingRequired.length} required field${missingFieldPlural} not mapped (${totalMapped}/${totalRequired})`
          }
        </Alert>
        {actionButton}
      </Box>

      {/* Missing fields — compact inline chips */}
      {validation.missingRequired.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
          {validation.missingRequired.map((field) => (
            <Chip
              key={field}
              label={getFieldDisplayName(field)}
              size="small"
              color="error"
              variant="outlined"
              icon={<ErrorIcon />}
              sx={{ fontSize: '0.7rem', height: 22 }}
            />
          ))}
        </Box>
      )}

      {/* Per-leg status */}
      {legCount > 1 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Fields by Leg:
          </Typography>
          {Array.from({ length: legCount }, (_, i) => i + 1).map((legNum) => (
            <Box key={legNum} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Chip
                label={`Leg ${legNum}`}
                size="small"
                color={validation.legStatus[legNum]?.complete ? 'success' : 'default'}
                sx={{ mr: 1 }}
              />
              <Typography variant="body2" color="text.secondary">
                {validation.legStatus[legNum]?.mappedCount}/{validation.legStatus[legNum]?.totalRequired} required fields
              </Typography>
            </Box>
          ))}
        </Box>
      )}

    </Box>
  );
};
